/**
 * Finish-First Selector Module for Murphy Door Configurator (Concept A)
 *
 * PURPOSE: Inverts the Wood & Finish flow in simplified (3Q) mode so the
 * customer picks a FINISH first, then the wood species that offers it — the
 * inverse of the default wood-first drill-down.
 *
 * PHASE: 2 (Event-Driven CCP - Strict Enforcement)
 * MODULE TYPE: UI (ui-) — designated DOM controller, authorized to render DOM
 * GOLDEN STANDARD REFERENCE: md-config-ui-tooltip-manager.js
 *
 * HOW IT CONVERGES WITH THE EXISTING ENGINE (zero new selection plumbing):
 * The standard Wood & Finish category is a two-level drill-down rendered into
 * `.murphy-door-configurator__thumbnails`: wood-product tiles (level 1) →
 * click a wood → that wood's finish VARIANT tiles (level 2) → click a finish →
 * `selection:browsing` on the resolved variant → implicit-apply promotes it to
 * `selection:applied` when the wizard advances (Wood & Finish is a
 * requiresApplyButton category).
 *
 * This module simply SWAPS the two levels:
 *   Level 1 → finish swatches (from PPD woodFinishInvertedIndex)
 *   Level 2 → the woods offering that finish, rendered as STANDARD variant
 *             tiles (`.murphy-door-configurator__thumbnail-item` + data-variant-id
 *             + data-product-id) so the EXISTING global click delegation in
 *             md-config-core-event-handler.js handles selection identically.
 * Each finish→wood pick resolves to the SAME single variant the wood-first flow
 * would have produced — only the ENTRY ORDER inverts. Cascade / forbidden /
 * material-preference logic is untouched.
 *
 * AUTO-SKIP: a finish offered by only one wood (paints / TFL / Natural) skips
 * the wood step — the module renders that single variant tile and
 * programmatically clicks it, applying in one tap.
 *
 * GATE: only active when `ppd.simplifiedUxConfig.enabled` is true. The standard
 * configurator and the post-completion (edit) flow stay wood-first.
 *
 * CCP COMPLIANCE:
 * - CONSTANTS.EVENTS.* exclusively (no local EVENT_TYPES - Pattern 19)
 * - publishEvent() helper with try/catch (Pattern 2)
 * - Bootstrap runtime protection via SYSTEM_STATE_FROM_PPD_READY (Pattern 6)
 * - PPD-first data access via conductor.getWoodFinishInvertedIndex() (Pattern 7/8)
 * - DOM rendering done HERE because this is a ui- module (designated controller)
 * - Selection delegated to the existing variant-tile click path (no peer calls)
 * - Cleanup arrays for all subscriptions/observers (Pattern 15)
 * - Initialization guard (Pattern 16)
 * - XSS: finish/wood names come from trusted Shopify admin metafields and are
 *   written via textContent / element properties only (never innerHTML)
 *
 * Dependencies:
 * - md-config-core-event-bus.js (event communication)
 * - md-config-core-conductor.js (getWoodFinishInvertedIndex delegation)
 */

/** Module uses scoped pattern for Shopify compatibility */
(function (window) {
  'use strict';

  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};

  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;
  const EVENTS = CONSTANTS && CONSTANTS.EVENTS;

  function isDev() {
    try {
      return !!(
        window.MurphyDoorConfigurator.utils &&
        window.MurphyDoorConfigurator.utils.isDevelopment &&
        window.MurphyDoorConfigurator.utils.isDevelopment()
      );
    } catch (e) {
      return false;
    }
  }

  function getConductor() {
    return window.MurphyDoorConfigurator.conductor || null;
  }

  function getEventBus() {
    return window.MurphyDoorConfigurator.CoreEventBus || null;
  }

  // ============================================================================
  // SELECTORS / CLASS NAMES
  // ============================================================================
  const SELECTORS = Object.freeze({
    TOOLS: '.murphy-door-configurator__tools',
    WF_CATEGORY:
      '.murphy-door-configurator__category-button[data-category="Wood & Finish"]',
    THUMBNAILS: '.murphy-door-configurator__thumbnails',
    THUMBNAIL_IMAGE: '.murphy-door-configurator__thumbnail-image',
    THUMBNAIL_CAPTION: '.murphy-door-configurator__thumbnail-caption',
  });

  const CLASS = Object.freeze({
    SWATCH_ROW: 'murphy-door-configurator__finish-swatch-row',
    SWATCH: 'murphy-door-configurator__finish-swatch',
    SWATCH_IMAGE: 'murphy-door-configurator__finish-swatch-image',
    SWATCH_LABEL: 'murphy-door-configurator__finish-swatch-label',
    SWATCH_COUNT: 'murphy-door-configurator__finish-swatch-count',
    BACK: 'murphy-door-configurator__finish-first-back',
    VARIANT_ITEM: 'murphy-door-configurator__variant-item',
    THUMBNAIL_ITEM: 'murphy-door-configurator__thumbnail-item',
    THUMBNAIL_IMAGE: 'murphy-door-configurator__thumbnail-image',
    THUMBNAIL_CAPTION: 'murphy-door-configurator__thumbnail-caption',
  });

  // data-attribute markers so the module can find/clean up its own DOM
  const DATA = Object.freeze({
    GENERATED: 'data-finish-first-generated', // value "swatch-row" | "wood-tile" | "back"
    ORIGINAL_HIDDEN: 'data-finish-first-original-hidden',
    FINISH_SKU: 'data-finish-sku',
  });

  const WOOD_AND_FINISH = 'Wood & Finish';
  const READY_RETRY_LIMIT = 40; // ~10s @ 250ms
  const READY_RETRY_DELAY = 250;

  // Canonical Previewing/Save status-icon overlay path data (save button +
  // circle-check), matching the standard configurator thumbnails. Built via DOM
  // construction (createElementNS) as a fallback when no existing tile is
  // available to clone from, so generated tiles ALWAYS carry the selected-state
  // affordances. Static, trusted constants (no user data, no innerHTML).
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const SAVE_ICON_PATH =
    'M18.1716 1C18.702 1 19.2107 1.21071 19.5858 1.58579L22.4142 4.41421C22.7893 4.78929 23 5.29799 23 5.82843V20C23 21.6569 21.6569 23 20 23H4C2.34315 23 1 21.6569 1 20V4C1 2.34315 2.34315 1 4 1H18.1716ZM4 3C3.44772 3 3 3.44772 3 4V20C3 20.5523 3.44772 21 4 21L5 21L5 15C5 13.3431 6.34315 12 8 12L16 12C17.6569 12 19 13.3431 19 15V21H20C20.5523 21 21 20.5523 21 20V6.82843C21 6.29799 20.7893 5.78929 20.4142 5.41421L18.5858 3.58579C18.2107 3.21071 17.702 3 17.1716 3H17V5C17 6.65685 15.6569 8 14 8H10C8.34315 8 7 6.65685 7 5V3H4ZM17 21V15C17 14.4477 16.5523 14 16 14L8 14C7.44772 14 7 14.4477 7 15L7 21L17 21ZM9 3H15V5C15 5.55228 14.5523 6 14 6H10C9.44772 6 9 5.55228 9 5V3Z';
  const CHECK_ICON_PATH =
    'M8 16C3.58172 16 0 12.4182 0 8C0 3.58172 3.58172 0 8 0C12.4182 0 16 3.58172 16 8C16 12.4182 12.4182 16 8 16ZM8 14.4C11.5346 14.4 14.4 11.5346 14.4 8C14.4 4.46538 11.5346 1.6 8 1.6C4.46538 1.6 1.6 4.46538 1.6 8C1.6 11.5346 4.46538 14.4 8 14.4ZM7.20208 11.2L3.80799 7.80592L4.93937 6.67448L7.20208 8.93728L11.7276 4.41178L12.859 5.54314L7.20208 11.2Z';

  // ============================================================================
  // MODULE STATE
  // ============================================================================
  let bootstrapComplete = false;
  let initialized = false;
  let activated = false; // finish-first mode currently rendered
  let currentFinishSku = null;
  let gridObserver = null;
  let modeObserver = null;
  let readyRetryCount = 0;
  let readyRetryTimer = null;
  const cleanupHandlers = [];

  // ============================================================================
  // EVENT PUBLISHING HELPER (Pattern 2)
  // ============================================================================
  function publishEvent(eventType, payload) {
    const bus = getEventBus();
    if (!bus) {
      isDev() &&
        console.warn(
          `[FinishFirst] CoreEventBus not available, failed to publish: ${eventType}`
        );
      return;
    }
    try {
      bus.publish(eventType, {
        ...payload,
        timestamp: Date.now(),
        source: 'FinishFirstSelector',
      });
    } catch (error) {
      console.error(`[FinishFirst] Failed to publish ${eventType}:`, error);
    }
  }

  // ============================================================================
  // GATING
  // ============================================================================
  function isSimplifiedEnabled() {
    const conductor = getConductor();
    if (!conductor) return false;
    const ppd =
      typeof conductor.getPerfectProductDictionary === 'function'
        ? conductor.getPerfectProductDictionary()
        : null;
    return !!(ppd && ppd.simplifiedUxConfig && ppd.simplifiedUxConfig.enabled);
  }

  function isSimplifiedActiveStep() {
    // Only invert during the active 3Q wizard, not in the post-completion
    // full-configurator/edit flow.
    const tools = document.querySelector(SELECTORS.TOOLS);
    if (!tools) return false;
    const mode = tools.getAttribute('data-simplified-mode');
    // Treat missing attribute as not-yet-stamped; require explicit "active".
    return mode === 'active';
  }

  // ============================================================================
  // DOM HELPERS
  // ============================================================================
  function getWfGrid() {
    const wf = document.querySelector(SELECTORS.WF_CATEGORY);
    if (!wf) return null;
    return wf.querySelector(SELECTORS.THUMBNAILS);
  }

  function normalizeImage(url) {
    if (!url) return '';
    if (url.indexOf('//') === 0) return 'https:' + url;
    return url;
  }

  function formatPrice(price) {
    // PPD variant.price is already a formatted dollar string (e.g. "1,190.00",
    // "500.00", "0.00") — NOT integer cents. Just prefix the currency symbol,
    // matching the existing thumbnail caption format ("Name $1,190").
    if (price == null) return '';
    const s = String(price).replace(/^\$/, '').trim();
    if (!s) return '';
    // Whole dollars drop the ".00"; real cents survive. Same shared rule as the
    // main total and the Summary rows — see utils.dropWholeDollarCents.
    const utils = window.MurphyDoorConfigurator.utils;
    return (
      '$' +
      (utils && utils.dropWholeDollarCents ? utils.dropWholeDollarCents(s) : s)
    );
  }

  /** Hide the pre-built wood-product tiles (the original level-1). */
  function hideOriginalTiles(grid) {
    Array.from(grid.children).forEach((child) => {
      if (child.hasAttribute(DATA.GENERATED)) return; // ours, skip
      if (child.getAttribute('data-product-id')) {
        child.setAttribute(DATA.ORIGINAL_HIDDEN, 'true');
        child.style.display = 'none';
      }
    });
  }

  /** Restore the original wood-product tiles and remove all generated DOM. */
  function restoreOriginalTiles(grid) {
    if (!grid) return;
    Array.from(grid.querySelectorAll(`[${DATA.GENERATED}]`)).forEach((el) =>
      el.remove()
    );
    Array.from(
      grid.querySelectorAll(`[${DATA.ORIGINAL_HIDDEN}="true"]`)
    ).forEach((el) => {
      el.removeAttribute(DATA.ORIGINAL_HIDDEN);
      el.style.display = '';
    });
  }

  function removeGeneratedWoodTilesAndBack(grid) {
    Array.from(
      grid.querySelectorAll(
        `[${DATA.GENERATED}="wood-tile"], [${DATA.GENERATED}="back"]`
      )
    ).forEach((el) => el.remove());
  }

  // ============================================================================
  // RENDER: LEVEL 1 — FINISH SWATCHES
  // ============================================================================
  function renderFinishSwatches(grid, index) {
    // Remove any prior generated content (idempotent re-render)
    removeGeneratedWoodTilesAndBack(grid);
    const priorRow = grid.querySelector(`[${DATA.GENERATED}="swatch-row"]`);
    if (priorRow) priorRow.remove();

    const row = document.createElement('div');
    row.className = CLASS.SWATCH_ROW;
    row.setAttribute(DATA.GENERATED, 'swatch-row');

    index.finishOrder.forEach((sku) => {
      const meta = index.finishMeta.get(sku);
      if (!meta) return;
      const woods = index.finishToWoods.get(sku) || [];

      if (woods.length === 1) {
        // 1-WOOD FINISH (every paint → Paint Grade, both TFL finishes → TFL,
        // Natural → White Oak): there is nothing to choose for the wood, so the
        // swatch IS the variant — a bubbled variant tile that selects in place
        // on click (Previewing/Save state shows on the swatch itself). NO
        // second step.
        const tile = buildVariantTile(woods[0], {
          label: meta.finishName,
          image: meta.sampleImage,
          showPrice: false,
          generatedType: 'finish-variant',
          finishSku: sku,
        });
        tile.setAttribute('aria-label', `${meta.finishName} finish`);
        row.appendChild(tile);
      } else {
        // MULTI-WOOD FINISH (the stains): a drill swatch → reveals the woods.
        row.appendChild(buildDrillSwatch(meta, sku));
      }
    });

    // Insert swatch row at the top of the grid (before hidden originals)
    grid.insertBefore(row, grid.firstChild);

    // Delegated click for DRILL swatches only (scoped to this row). 1-wood
    // finish-variant tiles are .thumbnail-item (not .finish-swatch), so this
    // handler ignores them and the click bubbles to the existing global
    // thumbnail delegation, which applies the variant in place.
    row.addEventListener('click', onSwatchRowClick);

    publishEvent(EVENTS.UI_FINISH_FIRST_RENDERED, {
      finishCount: index.finishOrder.length,
    });
  }

  /** A multi-wood finish swatch: clicking it reveals the woods (level 2). */
  function buildDrillSwatch(meta, sku) {
    const swatch = document.createElement('button');
    swatch.type = 'button';
    swatch.className = CLASS.SWATCH;
    swatch.setAttribute(DATA.FINISH_SKU, sku);
    swatch.setAttribute(DATA.GENERATED, 'drill-swatch');
    swatch.setAttribute(
      'aria-label',
      `${meta.finishName} — choose from ${meta.woodCount} woods`
    );

    const imgWrap = document.createElement('span');
    imgWrap.className = CLASS.SWATCH_IMAGE;
    const img = document.createElement('img');
    img.src = normalizeImage(meta.sampleImage);
    img.alt = meta.finishName;
    img.width = 100;
    img.height = 100;
    img.loading = 'lazy';
    imgWrap.appendChild(img);

    const label = document.createElement('span');
    label.className = CLASS.SWATCH_LABEL;
    label.textContent = meta.finishName;

    swatch.appendChild(imgWrap);
    swatch.appendChild(label);
    return swatch;
  }

  /**
   * Clone the Previewing/Save status-icon overlay (save button + circle-check)
   * from an existing thumbnail in the grid so generated tiles show the same
   * selected-state affordances the standard tiles do. The save button's handler
   * is document-delegated, so the clone stays fully functional.
   */
  function buildStatusIcons() {
    const grid = getWfGrid();
    const src =
      grid &&
      grid.querySelector('.murphy-door-configurator__thumbnail-status-icon');
    if (src) return src.cloneNode(true); // prefer the live markup (future-proof)
    return buildStatusIconsFromTemplate();
  }

  function svgNode(tag, attrs) {
    const el = document.createElementNS(SVG_NS, tag);
    Object.keys(attrs).forEach((k) => el.setAttribute(k, attrs[k]));
    return el;
  }

  /**
   * Build the Previewing/Save overlay via DOM construction (no innerHTML) so it
   * always exists even when there is no source tile to clone from. Mirrors the
   * standard `.murphy-door-configurator__thumbnail-status-icon` markup.
   */
  function buildStatusIconsFromTemplate() {
    const wrap = document.createElement('div');
    wrap.className = 'murphy-door-configurator__thumbnail-status-icon';
    // Decorative, and now nested inside a role="button" tile -- see the same
    // change in md-config-ui-navigation-manager.js and the pipeline builders.
    // A real <button> in there is nested-interactive, and its "Save this
    // configuration" label pointed at a control with no handler anywhere.
    wrap.setAttribute('aria-hidden', 'true');

    const saveBtn = document.createElement('span');
    saveBtn.className = 'thumbnail-save-button';
    const saveSvg = svgNode('svg', {
      width: '13',
      height: '12',
      viewBox: '0 0 24 24',
      class: 'icon icon-share',
      fill: 'none',
      'aria-hidden': 'true',
      focusable: 'false',
    });
    saveSvg.appendChild(
      svgNode('path', {
        'fill-rule': 'evenodd',
        'clip-rule': 'evenodd',
        d: SAVE_ICON_PATH,
        fill: 'currentColor',
      })
    );
    saveBtn.appendChild(saveSvg);

    const checkSpan = document.createElement('span');
    checkSpan.className = 'thumbnail-circle-check';
    const checkSvg = svgNode('svg', {
      width: '16',
      height: '16',
      viewBox: '0 0 16 16',
      fill: 'inherit',
      'aria-hidden': 'true',
      focusable: 'false',
    });
    checkSvg.appendChild(
      svgNode('circle', {
        class: 'md-circle-bg',
        cx: '8',
        cy: '8',
        r: '8',
        fill: 'transparent',
      })
    );
    checkSvg.appendChild(svgNode('path', { d: CHECK_ICON_PATH }));
    checkSpan.appendChild(checkSvg);

    wrap.appendChild(saveBtn);
    wrap.appendChild(checkSpan);
    return wrap;
  }

  function onSwatchRowClick(event) {
    const swatch = event.target.closest('.' + CLASS.SWATCH);
    if (!swatch) return; // 1-wood finish-variant tiles bubble to global handler
    // Drill swatch — our own UI; never let the global thumbnail delegation see it.
    event.stopPropagation();
    const sku = swatch.getAttribute(DATA.FINISH_SKU);
    if (sku) handleFinishPick(sku);
  }

  // ============================================================================
  // RENDER: LEVEL 2 — WOODS FOR THE CHOSEN FINISH (multi-wood finishes only)
  // ============================================================================
  // Only multi-wood finishes (the stains) reach this — 1-wood finishes are
  // directly selectable bubbled tiles at level 1, so they never drill.
  function handleFinishPick(finishSku) {
    const conductor = getConductor();
    const index = conductor && conductor.getWoodFinishInvertedIndex();
    const grid = getWfGrid();
    if (!index || !grid) return;

    const woods = index.finishToWoods.get(finishSku) || [];
    currentFinishSku = finishSku;

    publishEvent(EVENTS.UI_FINISH_FIRST_FINISH_SELECTED, {
      finishSku,
      woodCount: woods.length,
    });

    if (woods.length <= 1) {
      // Defensive: a 1-wood finish should never be a drill swatch. If we somehow
      // got here, do nothing rather than render a pointless single-wood step.
      isDev() &&
        console.warn(
          `[FinishFirst] Drill invoked for ${finishSku} with ${woods.length} woods — ignoring`
        );
      return;
    }

    // Hide finish swatches while showing woods
    const row = grid.querySelector(`[${DATA.GENERATED}="swatch-row"]`);
    if (row) row.style.display = 'none';
    removeGeneratedWoodTilesAndBack(grid);

    // back affordance + one variant tile per wood
    const back = buildBackButton();
    grid.appendChild(back);

    woods.forEach((entry) => {
      grid.appendChild(buildVariantTile(entry));
    });

    publishEvent(EVENTS.UI_FINISH_FIRST_WOODS_RENDERED, {
      finishSku,
      woodCount: woods.length,
    });
  }

  function buildBackButton() {
    const back = document.createElement('button');
    back.type = 'button';
    back.className = CLASS.BACK;
    back.setAttribute(DATA.GENERATED, 'back');
    back.setAttribute('aria-label', 'Back to finishes');
    back.textContent = '← Finishes';
    back.addEventListener('click', function (event) {
      event.stopPropagation();
      showFinishes();
    });
    return back;
  }

  /**
   * Build a "bubbled" variant tile that the existing global click delegation
   * (md-config-core-event-handler.js) applies DIRECTLY to the given variant.
   *
   * Why bubbled (data-bubbled + data-is-single-variant) rather than a plain
   * variant tile: a cold click on a MULTI-variant product tile makes the
   * handler resolve the product's DEFAULT variant (ignoring data-variant-id),
   * because the wood-first flow only honours a variant tile once the product
   * is already in a browsing context. The `data-bubbled="true"` flag
   * (event-handler.js: `resolvedVariantId = isBubbledVariant ? variantId :
   * null`) tells the handler to use THIS exact variant id and skip product
   * resolution — the same pattern material-preference-coordinator uses when a
   * preference resolves a product to a single compatible variant. Wood & Finish
   * is a requiresApplyButton category, so this still lands in `selection:browsing`
   * (not auto-apply) and implicit-apply promotes it when the wizard advances —
   * identical to the wood-first flow.
   */
  function buildVariantTile(entry, opts) {
    opts = opts || {};
    const labelText = opts.label || entry.woodName || '';
    const imageSrc = opts.image || entry.image;

    const tile = document.createElement('div');
    tile.className = CLASS.VARIANT_ITEM + ' ' + CLASS.THUMBNAIL_ITEM;
    tile.setAttribute('data-is-composite', 'false');
    tile.setAttribute('data-product-id', String(entry.productId));
    tile.setAttribute('data-variant-id', String(entry.variantId));
    tile.setAttribute('data-is-single-variant', 'true');
    tile.setAttribute('data-bubbled', 'true');
    tile.setAttribute(DATA.GENERATED, opts.generatedType || 'wood-tile');
    if (opts.finishSku) tile.setAttribute(DATA.FINISH_SKU, opts.finishSku);

    // P7 A11Y: same contract as the templated tiles in the navigation manager
    // and the PPD cache -- a focusable role="button" with a title+price label.
    // The delegated keydown funnel in md-config-core-event-handler.js matches
    // '.murphy-door-configurator__variant-item[role="button"]', and this tile
    // already carries VARIANT_ITEM, so the role is the only missing half.
    // Label is set below, once the price-aware caption text is computed --
    // setting it here would lose the price.
    tile.setAttribute('role', 'button');
    tile.setAttribute('tabindex', '0');
    // Always present, never absent: an omitted aria-pressed reads as "not a
    // toggle" rather than "not pressed", so a screen reader gives the customer
    // no way to tell chosen from unchosen. uiUpdater._setTileActive flips it.
    tile.setAttribute('aria-pressed', 'false');

    const imageWrap = document.createElement('div');
    imageWrap.className = CLASS.THUMBNAIL_IMAGE;
    const img = document.createElement('img');
    img.src = normalizeImage(imageSrc);
    img.alt = labelText;
    img.width = 100;
    img.height = 100;
    img.loading = 'lazy';
    imageWrap.appendChild(img);

    // Previewing/Save overlay so the tile shows the same selected-state
    // affordances as the standard thumbnails.
    const statusIcons = buildStatusIcons();
    if (statusIcons) imageWrap.appendChild(statusIcons);

    const caption = document.createElement('span');
    caption.className = CLASS.THUMBNAIL_CAPTION;
    const price = opts.showPrice === false ? '' : formatPrice(entry.price);
    caption.textContent = price ? `${labelText} ${price}` : labelText;
    // Reuse the caption text verbatim so the announced label and the visible
    // label cannot drift (WCAG 2.5.3 Label in Name). textContent, not
    // innerHTML -- no escaping concerns, the DOM API handles it.
    tile.setAttribute('aria-label', caption.textContent);

    tile.appendChild(imageWrap);
    tile.appendChild(caption);
    return tile;
  }

  function showFinishes() {
    const grid = getWfGrid();
    if (!grid) return;
    currentFinishSku = null;
    removeGeneratedWoodTilesAndBack(grid);
    const row = grid.querySelector(`[${DATA.GENERATED}="swatch-row"]`);
    if (row) row.style.display = '';
  }

  // ============================================================================
  // ACTIVATION
  // ============================================================================
  function enterFinishFirstMode() {
    if (activated) return;
    const conductor = getConductor();
    const index = conductor && conductor.getWoodFinishInvertedIndex();
    const grid = getWfGrid();

    if (!index || !grid) {
      publishEvent(EVENTS.UI_FINISH_FIRST_FAILED, {
        reason: !index ? 'no_inverted_index' : 'no_wf_grid',
      });
      isDev() &&
        console.warn(
          '[FinishFirst] Cannot activate — missing inverted index or WF grid'
        );
      return;
    }

    hideOriginalTiles(grid);
    renderFinishSwatches(grid, index);
    activated = true;

    // Safety net: if something re-renders the WF grid (e.g. material filter)
    // and wipes our swatches while still on the active wizard step, re-inject.
    attachGridObserver(grid);

    // Bound the inversion to the live 3Q wizard: when the wizard completes
    // (data-simplified-mode 'active' → 'complete'), tear down and hand the
    // Wood & Finish category back to the standard wood-first edit flow.
    attachModeObserver();

    isDev() &&
      console.log(
        `✅ [FinishFirst] Activated — ${index.finishOrder.length} finish swatches rendered`
      );
  }

  function attachModeObserver() {
    if (modeObserver || !('MutationObserver' in window)) return;
    const tools = document.querySelector(SELECTORS.TOOLS);
    if (!tools) return;
    modeObserver = new MutationObserver(() => {
      if (!activated) return;
      const mode = tools.getAttribute('data-simplified-mode');
      if (mode !== 'active') {
        teardownToWoodFirst();
      }
    });
    modeObserver.observe(tools, {
      attributes: true,
      attributeFilter: ['data-simplified-mode'],
    });
  }

  /**
   * Revert the Wood & Finish category to the standard wood-first flow: remove
   * all generated DOM, restore the original wood-product tiles, and stop the
   * grid/mode observers. Called when the 3Q wizard completes so the full
   * configurator's edit flow is 100% standard.
   */
  function teardownToWoodFirst() {
    // Cancel any in-flight activation poll first — a pending readyRetryTimer
    // could otherwise fire after teardown and re-enter enterFinishFirstMode()
    // on a config that is completing/complete.
    if (readyRetryTimer) {
      window.clearTimeout(readyRetryTimer);
      readyRetryTimer = null;
    }
    if (gridObserver) {
      gridObserver.disconnect();
      gridObserver = null;
    }
    if (modeObserver) {
      modeObserver.disconnect();
      modeObserver = null;
    }
    const grid = getWfGrid();
    if (grid) restoreOriginalTiles(grid);
    activated = false;
    currentFinishSku = null;
    isDev() &&
      console.log(
        '[FinishFirst] Wizard complete — reverted Wood & Finish to wood-first'
      );
  }

  function attachGridObserver(grid) {
    if (gridObserver || !('MutationObserver' in window)) return;
    gridObserver = new MutationObserver(() => {
      if (!activated) return;
      if (!isSimplifiedActiveStep()) return;
      const row = grid.querySelector(`[${DATA.GENERATED}="swatch-row"]`);
      const hasGenerated =
        row || grid.querySelector(`[${DATA.GENERATED}="wood-tile"]`);
      // Our DOM got wiped by an external re-render — rebuild from scratch.
      if (!hasGenerated) {
        const conductor = getConductor();
        const index = conductor && conductor.getWoodFinishInvertedIndex();
        if (!index) return;
        hideOriginalTiles(grid);
        renderFinishSwatches(grid, index);
        currentFinishSku = null;
      }
    });
    gridObserver.observe(grid, { childList: true });
  }

  /**
   * Poll for the active 3Q wizard step AND the WF grid's wood tiles, then
   * activate. Requires data-simplified-mode==='active' so a reload with a
   * RESTORED/COMPLETED config (mode==='complete') stays dormant (wood-first) —
   * the inversion is bounded to the live 3Q wizard, never the completed
   * full-configurator edit flow.
   */
  function waitForGridAndActivate() {
    if (activated) return;

    const tools = document.querySelector(SELECTORS.TOOLS);
    const grid = getWfGrid();
    const mode = tools && tools.getAttribute('data-simplified-mode');

    // Wizard already finished (e.g. restored config) — never invert.
    if (mode === 'complete') {
      isDev() &&
        console.log(
          '[FinishFirst] Simplified mode already complete — staying wood-first'
        );
      return;
    }

    const hasWoodTiles =
      grid && grid.querySelector('[data-product-id]') != null;

    // Only activate once the wizard is explicitly active and tiles exist.
    if (mode === 'active' && hasWoodTiles) {
      enterFinishFirstMode();
      return;
    }

    readyRetryCount += 1;
    if (readyRetryCount > READY_RETRY_LIMIT) {
      isDev() &&
        console.warn(
          '[FinishFirst] Gave up waiting for active 3Q step + Wood & Finish grid tiles'
        );
      return;
    }
    readyRetryTimer = window.setTimeout(
      waitForGridAndActivate,
      READY_RETRY_DELAY
    );
  }

  // ============================================================================
  // BOOTSTRAP BARRIER (Pattern 6)
  // ============================================================================
  function onBootstrapReady() {
    // Idempotency guard: the dual bootstrap barrier (event subscription +
    // immediate PPD-presence check) can invoke this twice on a tight load
    // race. Re-entry would restart waitForGridAndActivate() mid-poll and burn
    // the readyRetryCount budget. (Pattern 16)
    if (bootstrapComplete) return;
    bootstrapComplete = true;
    if (!isSimplifiedEnabled()) {
      isDev() &&
        console.log(
          '[FinishFirst] Simplified mode not enabled — staying dormant (wood-first)'
        );
      return;
    }
    waitForGridAndActivate();
  }

  // ============================================================================
  // INITIALIZATION (Pattern 16)
  // ============================================================================
  function initialize() {
    if (initialized) return;
    initialized = true;

    const bus = getEventBus();
    if (!bus || !EVENTS) {
      console.warn(
        '[FinishFirst] CoreEventBus/CONSTANTS unavailable — cannot initialize'
      );
      return;
    }

    cleanupHandlers.push(
      bus.subscribe(EVENTS.SYSTEM_STATE_FROM_PPD_READY, onBootstrapReady)
    );

    // If bootstrap already completed before this module loaded, run now.
    const conductor = getConductor();
    if (
      conductor &&
      typeof conductor.getPerfectProductDictionary === 'function' &&
      conductor.getPerfectProductDictionary()
    ) {
      onBootstrapReady();
    }

    isDev() && console.log('[FinishFirst] Initialized — awaiting bootstrap');
  }

  // ============================================================================
  // CLEANUP (Pattern 15)
  // ============================================================================
  function cleanup() {
    if (readyRetryTimer) {
      window.clearTimeout(readyRetryTimer);
      readyRetryTimer = null;
    }
    if (gridObserver) {
      gridObserver.disconnect();
      gridObserver = null;
    }
    if (modeObserver) {
      modeObserver.disconnect();
      modeObserver = null;
    }
    const grid = getWfGrid();
    if (grid) restoreOriginalTiles(grid);
    cleanupHandlers.forEach((unsub) => {
      try {
        typeof unsub === 'function' && unsub();
      } catch (e) {
        /* no-op */
      }
    });
    cleanupHandlers.length = 0;
    activated = false;
    initialized = false;
    currentFinishSku = null;
    isDev() && console.log('[FinishFirst] Cleanup complete');
  }

  // ============================================================================
  // EXPORT
  // ============================================================================
  window.MurphyDoorConfigurator.FinishFirstSelector = {
    initialize,
    cleanup,
    // exposed for debugging / live verification
    _enterFinishFirstMode: enterFinishFirstMode,
    _showFinishes: showFinishes,
    _isActivated: function () {
      return activated;
    },
  };

  // Robust init: don't depend on script load order relative to the event bus.
  // Poll briefly for CoreEventBus to exist, then initialize (which subscribes
  // to SYSTEM_STATE_FROM_PPD_READY AND runs immediately if the PPD is already
  // available — covering the case where bootstrap completed before we loaded).
  let initPollCount = 0;
  function checkDependenciesAndInit() {
    if (initialized) return;
    if (getEventBus() && CONSTANTS && CONSTANTS.EVENTS) {
      initialize();
      return;
    }
    initPollCount += 1;
    if (initPollCount > 200) return; // ~10s safety cap
    window.setTimeout(checkDependenciesAndInit, 50);
  }
  checkDependenciesAndInit();
})(window);
